import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { 
  FiPlus, 
  FiEdit2, 
  FiTrash2, 
  FiCheck, 
  FiX, 
  FiCreditCard,
  FiDollarSign,
  FiPercent,
  FiTrendingUp,
  FiRefreshCw
} from 'react-icons/fi';
import instanciaAxios, { esErrorPermiso } from '../../miscelaneo/instanciaAxios';
import PlantillaModal from '../util/PlantillaModal';
import SpinnerCargando from '../util/SpinnerCargando';
import { roundTo2, porcentajeDecimalADisplay, porcentajeDisplayADecimal } from '../../miscelaneo/monetario';

// Helper para formatear moneda
function formatearMoneda(valor) {
  return roundTo2(parseFloat(valor) || 0).toLocaleString('es-DO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

// =====================================================
// COMPONENTE: Modal Crear/Editar Pasarela
// =====================================================
function ModalPasarela({ abierto, onCerrar, pasarela, onGuardar }) {
  const [formData, setFormData] = useState({
    nombre: '',
    porcentaje_comision: '',
    monto_fijo_comision: '',
    id_cuenta_tesoreria: ''
  });
  const [guardando, setGuardando] = useState(false);
  const [cuentasBanco, setCuentasBanco] = useState([]);

  // Cargar cuentas de tesorería tipo banco
  useEffect(() => {
    if (abierto) {
      instanciaAxios.get('/cuenta_tesoreria/resumida')
        .then(res => {
          const bancos = (res.data || []).filter(c => c.id_tipo_cuenta_tesoreria === 2);
          setCuentasBanco(bancos);
        })
        .catch(() => setCuentasBanco([]));
    }
  }, [abierto]);

  useEffect(() => {
    if (pasarela) {
      setFormData({
        nombre: pasarela.nombre || '',
        porcentaje_comision: pasarela.porcentaje_display?.toString() || '',
        monto_fijo_comision: pasarela.monto_fijo_comision?.toString() || '0',
        id_cuenta_tesoreria: pasarela.id_cuenta_tesoreria?.toString() || ''
      });
    } else {
      setFormData({
        nombre: '',
        porcentaje_comision: '',
        monto_fijo_comision: '0',
        id_cuenta_tesoreria: ''
      });
    }
  }, [pasarela, abierto]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones
    if (!formData.nombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    const porcentaje = parseFloat(formData.porcentaje_comision);
    if (isNaN(porcentaje) || porcentaje < 0 || porcentaje > 100) {
      toast.error('El porcentaje debe estar entre 0 y 100');
      return;
    }

    const montoFijo = parseFloat(formData.monto_fijo_comision) || 0;
    if (montoFijo < 0) {
      toast.error('El monto fijo no puede ser negativo');
      return;
    }

    setGuardando(true);
    try {
      const payload = {
        nombre: formData.nombre.trim(),
        porcentaje_comision: porcentajeDisplayADecimal(porcentaje),
        monto_fijo_comision: montoFijo,
        id_cuenta_tesoreria: formData.id_cuenta_tesoreria ? parseInt(formData.id_cuenta_tesoreria) : null
      };

      if (pasarela?.id_pasarela_pago) {
        await instanciaAxios.put(`/pasarela-pago/${pasarela.id_pasarela_pago}`, payload);
        toast.success('Pasarela actualizada');
      } else {
        await instanciaAxios.post('/pasarela-pago', payload);
        toast.success('Pasarela creada');
      }
      
      onGuardar();
      onCerrar();
    } catch (error) {
      if (esErrorPermiso(error)) return;
      toast.error(error.response?.data?.mensaje || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <PlantillaModal
      mostrar={abierto}
      onClose={onCerrar}
      titulo={pasarela ? 'Editar Pasarela de Pago' : 'Nueva Pasarela de Pago'}
    >
      <div className="space-y-4">
        {/* Nombre */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Nombre de la Pasarela <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="nombre"
            value={formData.nombre}
            onChange={handleChange}
            placeholder="Ej: Cardnet, Azul, PayPal"
            className="w-full p-2 rounded-xl border-2 outline-none transition-all border-red-200 focus:border-red-500 focus:ring-2 focus:ring-red-100"
            autoFocus
          />
        </div>

        {/* Porcentaje de Comisión */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Porcentaje de Comisión (%) <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              type="number"
              name="porcentaje_comision"
              value={formData.porcentaje_comision}
              onChange={handleChange}
              placeholder="Ej: 3.5"
              step="0.01"
              min="0"
              max="100"
              className="w-full p-2 pr-10 rounded-xl border-2 outline-none transition-all border-red-200 focus:border-red-500 focus:ring-2 focus:ring-red-100"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
              <FiPercent size={18} />
            </span>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Porcentaje que cobra la pasarela por transacción
          </p>
        </div>

        {/* Monto Fijo */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Monto Fijo por Transacción (RD$)
          </label>
          <div className="relative">
            <input
              type="number"
              name="monto_fijo_comision"
              value={formData.monto_fijo_comision}
              onChange={handleChange}
              placeholder="0.00"
              step="0.01"
              min="0"
              className="w-full p-2 pr-10 rounded-xl border-2 outline-none transition-all border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
              <FiDollarSign size={18} />
            </span>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Monto fijo adicional por cada transacción (opcional)
          </p>
        </div>

        {/* Cuenta de Tesorería (Banco) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Cuenta de Tesorería (Banco)
          </label>
          <select
            name="id_cuenta_tesoreria"
            value={formData.id_cuenta_tesoreria}
            onChange={handleChange}
            className="w-full p-2 rounded-xl border-2 outline-none transition-all border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          >
            <option value="">-- Sin cuenta vinculada --</option>
            {cuentasBanco.map(cuenta => (
              <option key={cuenta.id_cuenta_tesoreria} value={cuenta.id_cuenta_tesoreria}>
                {cuenta.descripcion}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            Cuenta bancaria donde se depositan los pagos de esta pasarela
          </p>
        </div>

        {/* Preview de Comisión */}
        <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
          <p className="text-sm text-gray-600 mb-2">Vista previa de comisión:</p>
          <div className="text-sm">
            <span className="text-gray-500">En una venta de RD$ 1,000:</span>
            <span className="ml-2 font-semibold text-color-primario">
              RD$ {formatearMoneda(
                (1000 * (parseFloat(formData.porcentaje_comision) || 0) / 100) + 
                (parseFloat(formData.monto_fijo_comision) || 0)
              )}
            </span>
          </div>
        </div>

        {/* Botón */}
        <button
          type="button"
          onClick={handleSubmit}
          disabled={guardando}
          className="w-full bg-color-secundario text-white py-3 rounded-xl font-semibold hover:bg-opacity-90 transition-all disabled:opacity-50"
        >
          {guardando ? 'Guardando...' : (pasarela ? 'Actualizar Pasarela' : 'Crear Pasarela')}
        </button>
      </div>
    </PlantillaModal>
  );
}

// =====================================================
// COMPONENTE: Tarjeta de Resumen
// =====================================================
function TarjetaResumen({ titulo, valor, subtitulo, colorBorde, colorTexto }) {
  return (
    <div className={`bg-white rounded-xl border border-gray-300 shadow-md p-4 border-l-4 ${colorBorde}`}>
      <p className="text-sm text-gray-500">{titulo}</p>
      <p className={`text-2xl font-bold mt-1 ${colorTexto}`}>{valor}</p>
      {subtitulo && <p className="text-xs text-gray-400 mt-1">{subtitulo}</p>}
    </div>
  );
}

// =====================================================
// COMPONENTE PRINCIPAL: Pantalla Pasarelas de Pago
// =====================================================
export default function PantallaPasarelasPago() {
  const [pasarelas, setPasarelas] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [pasarelaEditar, setPasarelaEditar] = useState(null);
  const [mostrarInactivas, setMostrarInactivas] = useState(false);

  // Cargar datos
  const cargarDatos = useCallback(async () => {
    setCargando(true);
    try {
      const [pasarelasRes, resumenRes] = await Promise.all([
        instanciaAxios.get('/pasarela-pago', { 
          params: { incluir_inactivas: mostrarInactivas } 
        }),
        instanciaAxios.get('/pasarela-pago/resumen-comisiones')
      ]);
      
      setPasarelas(pasarelasRes.data);
      setResumen(resumenRes.data);
    } catch (error) {
      if (esErrorPermiso(error)) return;
      toast.error('Error al cargar pasarelas');
      console.error(error);
    } finally {
      setCargando(false);
    }
  }, [mostrarInactivas]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  // Handlers
  const handleNueva = () => {
    setPasarelaEditar(null);
    setModalAbierto(true);
  };

  const handleEditar = (pasarela) => {
    setPasarelaEditar(pasarela);
    setModalAbierto(true);
  };

  const handleToggleActivo = async (pasarela) => {
    try {
      if (pasarela.activo) {
        await instanciaAxios.delete(`/pasarela-pago/${pasarela.id_pasarela_pago}`);
        toast.success('Pasarela desactivada');
      } else {
        await instanciaAxios.put(`/pasarela-pago/${pasarela.id_pasarela_pago}`, { activo: true });
        toast.success('Pasarela activada');
      }
      cargarDatos();
    } catch (error) {
      if (esErrorPermiso(error)) return;
      toast.error('Error al cambiar estado');
    }
  };

  if (cargando && pasarelas.length === 0) {
    return <SpinnerCargando />;
  }

  return (
    <div className="bg-gray-200 min-h-screen">
      <div className="flex justify-center py-10">
        <div className="bg-gray-100 bg-opacity-90 rounded-xl border border-gray-100 p-12 w-full max-w-7xl mx-10">
          <div className="flex flex-col gap-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="font-cascadia font-bold text-2xl flex items-center gap-3">
            <FiCreditCard className="text-color-primario" />
            Pasarelas de Pago
          </h1>
          <p className="text-gray-500">
            Configura las pasarelas y sus comisiones por transacción
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={cargarDatos}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
            title="Actualizar"
          >
            <FiRefreshCw size={20} className={cargando ? 'animate-spin' : ''} />
          </button>
          
          <button
            onClick={handleNueva}
            className="flex items-center gap-2 px-4 py-2.5 bg-color-primario text-white rounded-xl hover:bg-opacity-90 transition-colors shadow-sm"
          >
            <FiPlus size={20} />
            Nueva Pasarela
          </button>
        </div>
      </div>

      {/* Resumen de Comisiones */}
      {resumen && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800">
              Resumen de Comisiones
            </h2>
            <span className="text-sm text-gray-500">
              {resumen.periodo.fecha_desde} al {resumen.periodo.fecha_hasta}
            </span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <TarjetaResumen
              titulo="Total Comisiones"
              valor={`RD$ ${formatearMoneda(resumen.totales.total_comisiones)}`}
              subtitulo="Asumidas por la empresa"
              colorBorde="border-l-red-500"
              colorTexto="text-red-600"
            />
            <TarjetaResumen
              titulo="Transacciones"
              valor={resumen.totales.cantidad_transacciones.toLocaleString()}
              subtitulo="Con tarjeta este mes"
              colorBorde="border-l-color-primario"
              colorTexto="text-gray-800"
            />
            <TarjetaResumen
              titulo="Volumen Procesado"
              valor={`RD$ ${formatearMoneda(resumen.totales.total_transacciones)}`}
              subtitulo="Total en tarjetas"
              colorBorde="border-l-green-500"
              colorTexto="text-green-600"
            />
            <TarjetaResumen
              titulo="Comisión Promedio"
              valor={`${resumen.totales.porcentaje_promedio}%`}
              subtitulo="Sobre el volumen"
              colorBorde="border-l-amber-500"
              colorTexto="text-amber-600"
            />
          </div>
        </div>
      )}

      {/* Tabla de Pasarelas */}
      <div className="bg-white rounded-xl border border-gray-300 shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="font-semibold text-gray-800">Pasarelas Configuradas</h2>
          
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={mostrarInactivas}
              onChange={(e) => setMostrarInactivas(e.target.checked)}
              className="rounded border-gray-300 text-color-primario focus:ring-color-primario"
            />
            Mostrar inactivas
          </label>
        </div>

        {pasarelas.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <FiCreditCard className="mx-auto text-gray-300 mb-4" size={48} />
            <p className="text-gray-500">No hay pasarelas configuradas</p>
            <button
              onClick={handleNueva}
              className="mt-4 text-color-primario hover:underline font-medium"
            >
              Crear primera pasarela
            </button>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-color-primario text-white">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">
                  Pasarela
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider">
                  Comisión %
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider">
                  Monto Fijo
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider">
                  Cuenta Banco
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider">
                  Transacciones
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider">
                  Comisiones Mes
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {pasarelas.map((pasarela) => (
                <tr 
                  key={pasarela.id_pasarela_pago}
                  className={`hover:bg-gray-50 transition-colors ${!pasarela.activo ? 'opacity-50' : ''}`}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <FiCreditCard className="text-color-primario" size={20} />
                      </div>
                      <span className="font-medium text-gray-900">{pasarela.nombre}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="font-mono text-gray-700">
                      {pasarela.porcentaje_display}%
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="font-mono text-gray-700">
                      {pasarela.monto_fijo_comision > 0 
                        ? `RD$ ${formatearMoneda(pasarela.monto_fijo_comision)}`
                        : '-'
                      }
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    {pasarela.nombre_cuenta_tesoreria ? (
                      <span className="text-sm text-gray-700">{pasarela.nombre_cuenta_tesoreria}</span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                        Sin vincular
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="text-gray-600">
                      {pasarela.cantidad_transacciones.toLocaleString()}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="font-medium text-rose-600">
                      RD$ {formatearMoneda(pasarela.total_comisiones)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                      pasarela.activo 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {pasarela.activo ? (
                        <>
                          <FiCheck size={12} />
                          Activa
                        </>
                      ) : (
                        <>
                          <FiX size={12} />
                          Inactiva
                        </>
                      )}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleEditar(pasarela)}
                        className="p-2 text-gray-400 hover:text-color-primario hover:bg-blue-50 rounded-lg transition-colors"
                        title="Editar"
                      >
                        <FiEdit2 size={18} />
                      </button>
                      <button
                        onClick={() => handleToggleActivo(pasarela)}
                        className={`p-2 rounded-lg transition-colors ${
                          pasarela.activo
                            ? 'text-gray-400 hover:text-rose-600 hover:bg-rose-50'
                            : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
                        }`}
                        title={pasarela.activo ? 'Desactivar' : 'Activar'}
                      >
                        {pasarela.activo ? <FiTrash2 size={18} /> : <FiCheck size={18} />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

          </div>
        </div>
      </div>

      {/* Modal */}
      <ModalPasarela
        abierto={modalAbierto}
        onCerrar={() => setModalAbierto(false)}
        pasarela={pasarelaEditar}
        onGuardar={cargarDatos}
      />
    </div>
  );
}
